import {
  Spinner,
  HorizontalSlider,
  IconButton,
  CurtainsOpen,
  CurtainsClose,
  CurtainsLeft,
  CurtainsRight,
  Favourite,
} from '@mgh-app/component-library';
import { useState, useEffect } from 'react';
import { useGetShadesValuesQuery, useSetShadesValueMutation } from 'services/ThingsService';
import { POLLING_INTERVAL } from 'services/apiConfig';
import {
  alignItems,
  backgroundColor,
  borderRadius,
  boxShadow,
  display,
  flexDirection,
  fontSize,
  gap,
  gridTemplateRows,
  height,
  inset,
  justifyContent,
  margin,
  maxHeight,
  maxWidth,
  padding,
  pointerEvents,
  position,
  tailwindClassNameConvertNegative,
  textColor,
  TMargin,
  TTranslate,
  twCls,
  width,
} from 'style';

import curtainsBackground from 'assets/curtainsBackground.svg';

export interface CurtainSetControllerProps {
  curtainSetId: string;
}

export const CurtainSetController = ({ curtainSetId }: CurtainSetControllerProps): JSX.Element => {
  const {
    data: curtain,
    isLoading,
    isError,
  } = useGetShadesValuesQuery(curtainSetId, {
    pollingInterval: POLLING_INTERVAL,
    refetchOnMountOrArgChange: true,
  });
  const [setCurtainValue] = useSetShadesValueMutation();

  const [curtainPosition, setCurtainPosition] = useState<number>(0);
  const [favourite, setFavourite] = useState<number>(0);

  const onPositionChange = (_: Event, newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    setCurtainPosition(value);
  };

  const onPositionChangeCommitted = (newValue: number | number[]) => {
    if (curtain) {
      const value = Array.isArray(newValue) ? newValue[0] : newValue;
      setCurtainValue({ ...curtain, position: value, favourite: favourite });
    }
  };

  const openCurtains = () => {
    if (curtain) {
      setCurtainPosition(0);
      setCurtainValue({ ...curtain, position: 0, favourite: favourite });
    }
  };

  const closeCurtains = () => {
    if (curtain) {
      setCurtainPosition(100);
      setCurtainValue({ ...curtain, position: 100, favourite: favourite });
    }
  };

  const moveCurtainsLeft = () => {
    if (curtain && curtainPosition > 0) {
      const newPosition = curtainPosition - 1;
      setCurtainPosition(newPosition);
      setCurtainValue({ ...curtain, position: newPosition, favourite: favourite });
    }
  };

  const moveCurtainsRight = () => {
    if (curtain && curtainPosition < 100) {
      const newPosition = curtainPosition + 1;
      setCurtainPosition(newPosition);
      setCurtainValue({ ...curtain, position: newPosition, favourite: favourite });
    }
  };

  const onFavouriteChange = (_: Event, newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    setFavourite(value);
  };

  const onFavouriteChangeCommitted = (newValue: number | number[]) => {
    if (curtain) {
      const value = Array.isArray(newValue) ? newValue[0] : newValue;
      setCurtainValue({ ...curtain, position: curtainPosition, favourite: value });
    }
  };

  const setFavouritePosition = () => {
    if (curtain) {
      setCurtainPosition(favourite);
      setCurtainValue({ ...curtain, position: favourite, favourite: favourite });
    }
  };

  useEffect(() => {
    if (curtain) {
      setCurtainPosition(curtain.position);
      setFavourite(curtain.favourite);
    }
  }, [curtain]);

  return (
    <>
      {isError ? (
        <>Could not load curtains</>
      ) : !isLoading && curtain ? (
        <div className={twCls(display('grid'), justifyContent('justify-center'), padding('px-4'))}>
          <div
            className={twCls(
              display('grid'),
              gridTemplateRows('grid-rows-fc-min-2'),
              width('w-11/12'),
              maxWidth('max-w-xs'),
            )}
          >
            <div
              className={twCls(
                display('grid'),
                maxHeight('max-h-96'),
                boxShadow('shadow-shades-outer'),
              )}
            >
              <div className={twCls(position('relative'), display('grid'))}>
                <HorizontalSlider
                  sx={{
                    color: 'white',
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    '& .MuiSlider-rail': {
                      borderColor: 'white',
                      borderWidth:
                        curtainPosition === 0
                          ? '0.5rem 0.5rem 0.5rem 0.5rem'
                          : curtainPosition === 100
                          ? '0'
                          : '0.5rem 0.5rem 0.5rem 0',
                      borderRadius: 0,
                      backgroundColor: 'white',
                      boxShadow: 'inset 0pt 0pt 14pt 8pt rgba(87,127,255,0.35)',
                    },
                    '& .MuiSlider-track': {
                      borderColor: 'transparent',
                      borderRadius: 0,
                      backgroundImage: `url(${curtainsBackground})`,
                      backgroundPosition: 'stretch', // TODO: change to 'center' to have cool animation
                      backgroundSize: 'cover',
                      backgroundRepeat: 'no-repeat',
                    },
                    '& .MuiSlider-thumb': {
                      display: 'none',
                    },
                  }}
                  valueLabelDisplay="off"
                  value={curtainPosition}
                  onChange={onPositionChange}
                  onChangeCommitted={(_, val) => onPositionChangeCommitted(val)}
                />
                <div
                  className={twCls(
                    position('absolute'),
                    inset('top-1/2', 'left-1/2'),
                    tailwindClassNameConvertNegative<TTranslate>('-translate-x-1/2'),
                    tailwindClassNameConvertNegative<TTranslate>('-translate-y-1/2'),
                    display('flex'),
                    flexDirection('flex-col'),
                    alignItems('items-center'),
                    gap('gap-y-4'),
                    pointerEvents('pointer-events-none'),
                  )}
                >
                  <div
                    className={twCls(
                      height('h-20'),
                      width('w-20'),
                      backgroundColor('bg-white-opacity'),
                      borderRadius('rounded-full'),
                      textColor('text-blue-100'),
                      fontSize('text-3xl'),
                      display('flex'),
                      alignItems('items-center'),
                      justifyContent('justify-center'),
                    )}
                  >
                    {curtainPosition}%
                  </div>
                  <div
                    className={twCls(
                      width('w-max'),
                      padding('px-3'),
                      backgroundColor('bg-white-opacity'),
                      borderRadius('rounded-xl'),
                      textColor('text-grey-700'),
                      fontSize('text-base'),
                      display('flex'),
                      alignItems('items-center'),
                      justifyContent('justify-center'),
                    )}
                  >
                    Drag left or right to control
                  </div>
                </div>
              </div>
            </div>

            <div
              className={twCls(
                display('flex'),
                justifyContent('justify-between'),
                margin('mt-4'),
                tailwindClassNameConvertNegative<TMargin>('-mx-2'),
              )}
            >
              <IconButton onClick={openCurtains} className={twCls(padding('p-0'))}>
                <CurtainsOpen />
              </IconButton>
              <IconButton onClick={closeCurtains} className={twCls(padding('p-0'))}>
                <CurtainsClose />
              </IconButton>
              <IconButton
                onClick={moveCurtainsLeft}
                className={twCls(padding('p-0'))}
                disabled={curtainPosition === 0}
              >
                <CurtainsLeft />
              </IconButton>
              <IconButton
                onClick={moveCurtainsRight}
                className={twCls(padding('p-0'))}
                disabled={curtainPosition === 100}
              >
                <CurtainsRight />
              </IconButton>
            </div>

            <div
              className={twCls(
                display('flex'),
                alignItems('items-center'),
                margin('mt-4'),
                tailwindClassNameConvertNegative<TMargin>('-mx-2'),
              )}
            >
              <IconButton
                className={twCls(margin('mt-1'), padding('p-0'))}
                onClick={setFavouritePosition}
              >
                <Favourite />
              </IconButton>
              <HorizontalSlider
                className="mx-2"
                value={favourite}
                onChange={onFavouriteChange}
                onChangeCommitted={(_, val) => onFavouriteChangeCommitted(val)}
              />
              <span className={twCls(textColor('text-blue-100'), fontSize('text-xl'))}>
                {favourite}%
              </span>
            </div>
          </div>
        </div>
      ) : (
        <Spinner />
      )}
    </>
  );
};
